import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Input } from '@vaiu/ui/input';
import { Button } from '@vaiu/ui/button';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Badge } from '@vaiu/ui/badge';
import { Separator } from '@vaiu/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import { 
  Phone, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertCircle,
  Send,
  Trash2,
  RefreshCw
} from 'lucide-react';

interface PhoneStatus {
  phoneNumber: string | null;
  verified: boolean;
}

interface PhoneNumberManagerProps {
  initialPhoneNumber?: string | null;
  initialVerified?: boolean;
}

const PhoneNumberManager: React.FC<PhoneNumberManagerProps> = ({ 
  initialPhoneNumber = null,
  initialVerified = false 
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState<PhoneStatus>({ 
    phoneNumber: initialPhoneNumber, 
    verified: initialVerified 
  });
  const [phoneInput, setPhoneInput] = useState(initialPhoneNumber || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'loading' | 'setup' | 'verify' | 'complete'>(
    initialPhoneNumber && initialVerified ? 'complete' :
    initialPhoneNumber && !initialVerified ? 'verify' :
    'setup'
  );
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [codeExpiresIn, setCodeExpiresIn] = useState<number | null>(null);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);

  // Update state when props change
  useEffect(() => {
    setPhoneStatus({ 
      phoneNumber: initialPhoneNumber, 
      verified: initialVerified 
    });
    if (initialPhoneNumber) {
      setPhoneInput(initialPhoneNumber);
    }
    setStep(
      initialPhoneNumber && initialVerified ? 'complete' :
      initialPhoneNumber && !initialVerified ? 'verify' :
      'setup'
    );
  }, [initialPhoneNumber, initialVerified]);

  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, []);

  useEffect(() => {
    // Start countdown timer when code is sent
    if (codeExpiresIn && codeExpiresIn > 0) {
      const interval = setInterval(() => {
        setCodeExpiresIn(prev => {
          if (prev && prev > 0) {
            return prev - 1;
          } else {
            clearInterval(interval);
            return null;
          }
        });
      }, 1000);
      setTimerInterval(interval);
      
      return () => clearInterval(interval);
    }
  }, [codeExpiresIn]);

  // Removed loadPhoneStatus - now using props from parent component
  // The profile page loads the full profile including telephone field
  // and passes it down as props to avoid duplicate API calls

  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    
    // Ensure it starts with + for country code
    if (cleaned && !cleaned.startsWith('+')) {
      // Default to US if no country code
      cleaned = '+1' + cleaned;
    }
    
    return cleaned;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Basic E.164 format validation
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phone);
  };

  const checkAvailability = async () => {
    const formattedPhone = formatPhoneNumber(phoneInput);
    
    if (!validatePhoneNumber(formattedPhone)) {
      setAvailabilityMessage('Please enter a valid phone number with country code (e.g., +1234567890)');
      return;
    }

    try {
      setCheckingAvailability(true);
      setAvailabilityMessage('');
      
      const response = await platformApi.profile.checkPhoneAvailability(formattedPhone);
      
      if (response.available) {
        setAvailabilityMessage('✓ Phone number is available');
        setPhoneInput(formattedPhone);
      } else {
        setAvailabilityMessage('✗ This phone number is already registered to another account');
      }
    } catch (error: any) {
      console.error('Failed to check availability:', error);
      setAvailabilityMessage('Failed to check availability. Please try again.');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSetPhone = async () => {
    const formattedPhone = formatPhoneNumber(phoneInput);
    
    if (!validatePhoneNumber(formattedPhone)) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number with country code (e.g., +1234567890)',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSaving(true);
      
      // First check availability
      const availabilityResponse = await platformApi.profile.checkPhoneAvailability(formattedPhone);
      
      if (!availabilityResponse.available) {
        toast({
          title: 'Phone Not Available',
          description: 'This phone number is already registered to another account.',
          variant: 'destructive'
        });
        setAvailabilityMessage('✗ This phone number is already registered');
        return;
      }
      
      // Set the phone number
      const setResponse = await platformApi.profile.setPhoneNumber(formattedPhone);
      
      if (setResponse.phoneNumber) {
        setPhoneStatus({
          phoneNumber: setResponse.phoneNumber,
          verified: false
        });
        
        // Immediately send verification code
        await sendVerificationCode();
        
        setStep('verify');
        toast({
          title: 'Phone Number Added',
          description: 'Verification code has been sent to your phone.'
        });
      }
    } catch (error: any) {
      console.error('Failed to set phone number:', error);
      
      // Handle different error status codes
      if (error.status === 409) {
        // Phone already taken
        toast({
          title: 'Phone Already in Use',
          description: 'This phone number is registered to another account.',
          variant: 'destructive'
        });
        setPhoneInput('');
        setAvailabilityMessage('');
      } else if (error.status === 400) {
        // Bad request - invalid phone format or other validation error
        toast({
          title: 'Invalid Phone Number',
          description: error.message || 'Please check the phone number format.',
          variant: 'destructive'
        });
      } else if (error.status === 401) {
        // Unauthorized - session issue
        toast({
          title: 'Session Expired',
          description: 'Please log in again.',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Failed to Set Phone Number',
          description: error.message || 'Please try again.',
          variant: 'destructive'
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const sendVerificationCode = async () => {
    try {
      setSendingCode(true);
      
      const response = await platformApi.profile.sendPhoneVerification();
      
      if (response.success) {
        setCodeExpiresIn(response.expiresIn || 600); // Default to 10 minutes
        toast({
          title: 'Verification Code Sent',
          description: `Code sent to ${response.phoneNumber || phoneStatus.phoneNumber}`
        });
      }
    } catch (error: any) {
      console.error('Failed to send verification code:', error);
      toast({
        title: 'Failed to Send Code',
        description: error.message || 'Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: 'Invalid Code',
        description: 'Please enter the 6-digit verification code.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setVerifying(true);
      
      const response = await platformApi.profile.verifyPhoneNumber(verificationCode);
      
      if (response.verified) {
        setPhoneStatus({
          phoneNumber: phoneStatus.phoneNumber,
          verified: true
        });
        setStep('complete');
        setVerificationCode('');
        setCodeExpiresIn(null);
        
        toast({
          title: 'Phone Verified!',
          description: 'Your phone number has been verified successfully.'
        });
      }
    } catch (error: any) {
      console.error('Failed to verify phone:', error);
      
      // Handle different error status codes based on new API behavior
      let errorTitle = 'Verification Failed';
      let errorMessage = 'Please try again.';
      
      if (error.status === 400) {
        // Invalid/expired code, missing code, or no phone to verify
        errorTitle = 'Invalid or Expired Code';
        errorMessage = error.message || 'Please check the code or request a new one.';
      } else if (error.status === 401) {
        // Actually unauthorized - session issue
        errorTitle = 'Session Expired';
        errorMessage = 'Please log in again.';
        // Note: BaseApiClient should handle this automatically
      } else if (error.status === 409) {
        // Phone already taken
        errorTitle = 'Phone Number Unavailable';
        errorMessage = 'This phone number is already registered to another account.';
        // Reset the phone number field
        setPhoneNumber('');
        setStep('input');
        setVerificationCode('');
      } else {
        errorMessage = error.message || 'Please try again.';
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleRemovePhone = async () => {
    if (!confirm('Are you sure you want to remove your phone number? You will lose SMS notification capabilities.')) {
      return;
    }

    try {
      setSaving(true);
      
      await platformApi.profile.removePhoneNumber();
      
      setPhoneStatus({ phoneNumber: null, verified: false });
      setPhoneInput('');
      setVerificationCode('');
      setStep('setup');
      setCodeExpiresIn(null);
      
      toast({
        title: 'Phone Number Removed',
        description: 'Your phone number has been removed from your account.'
      });
    } catch (error: any) {
      console.error('Failed to remove phone:', error);
      toast({
        title: 'Failed to Remove Phone',
        description: error.message || 'Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const canResendCode = (): boolean => {
    return !codeExpiresIn || codeExpiresIn <= 60; // Can resend with 1 minute left or expired
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Phone className="w-5 h-5 mr-2" />
            Phone Number
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Phone className="w-5 h-5 mr-2" />
          Phone Number
        </CardTitle>
        <CardDescription>
          Manage your phone number for SMS notifications and two-factor authentication
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Status */}
        {phoneStatus.phoneNumber && (
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Phone className="w-4 h-4 text-gray-600" />
              <span className="font-medium">{phoneStatus.phoneNumber}</span>
              {phoneStatus.verified ? (
                <Badge variant="default" className="bg-green-600">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              ) : (
                <Badge variant="secondary">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Unverified
                </Badge>
              )}
            </div>
            {step === 'complete' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemovePhone}
                disabled={saving}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}

        {/* Setup Step */}
        {step === 'setup' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1234567890"
                value={phoneInput}
                onChange={(e) => {
                  setPhoneInput(e.target.value);
                  setAvailabilityMessage('');
                }}
                disabled={saving || checkingAvailability}
              />
              <p className="text-sm text-gray-500">
                Include country code (e.g., +1 for US, +44 for UK)
              </p>
              {availabilityMessage && (
                <p className={`text-sm ${availabilityMessage.includes('✓') ? 'text-green-600' : 'text-red-600'}`}>
                  {availabilityMessage}
                </p>
              )}
            </div>

            <Button
              onClick={handleSetPhone}
              disabled={!phoneInput || saving}
              className="w-full"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting Phone Number...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Add Phone & Send Verification
                </>
              )}
            </Button>
          </div>
        )}

        {/* Verification Step */}
        {step === 'verify' && (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                A verification code has been sent to {phoneStatus.phoneNumber}. 
                Please enter it below to verify your phone number.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                disabled={verifying}
              />
              {codeExpiresIn !== null && codeExpiresIn > 0 && (
                <p className="text-sm text-gray-500">
                  Code expires in {formatTime(codeExpiresIn)}
                </p>
              )}
              {codeExpiresIn === 0 && (
                <p className="text-sm text-red-600">
                  Code has expired. Please request a new one.
                </p>
              )}
            </div>

            <div className="flex space-x-2">
              <Button
                onClick={handleVerify}
                disabled={!verificationCode || verificationCode.length !== 6 || verifying}
                className="flex-1"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verify
                  </>
                )}
              </Button>
              
              <Button
                variant="outline"
                onClick={sendVerificationCode}
                disabled={!canResendCode() || sendingCode}
              >
                {sendingCode ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>

            <Separator />
            
            <Button
              variant="ghost"
              onClick={() => {
                setStep('setup');
                setPhoneInput('');
                setVerificationCode('');
                setCodeExpiresIn(null);
              }}
              className="w-full"
            >
              Change Phone Number
            </Button>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && (
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Your phone number is verified and ready to receive SMS notifications.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <p className="text-sm text-gray-600">Features available:</p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• SMS notifications for important events</li>
                <li>• Two-factor authentication (if enabled)</li>
                <li>• Account recovery via SMS</li>
                <li>• Payment confirmations</li>
              </ul>
            </div>

            <Button
              variant="outline"
              onClick={() => {
                setStep('setup');
                setPhoneInput('');
              }}
              className="w-full"
            >
              Change Phone Number
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PhoneNumberManager;
